import { useLocation } from 'react-router';
import { api } from './api/client';
import { useAsync } from './ui/useAsync';
import { useLiveVersion } from './live';

export interface Badges {
  /** Solicitudes para unirse a la iglesia, por aprobar. */
  members: number;
  /** Aprobaciones pendientes (inscripciones y solicitudes) más las solicitudes para unirse. */
  approvals: number;
  /** Mensajes sin leer en Chats (suma de lo que cada conversación trae sin leer). */
  chats: number;
}

/** Globitos con lo pendiente; se actualizan solos con el canal en vivo. */
export function useBadges(me: { activeChurchId: string | null; personId: string; can: (p: string) => boolean }): Badges {
  const churchId = me.activeChurchId;
  const canChat = me.can('church.read');
  // Chats no avisa en vivo: se vuelve a contar al cambiar de pantalla (así baja al salir de un chat).
  const { pathname } = useLocation();
  const chatsAt = pathname.startsWith('/app/chats') ? pathname : 'fuera';
  const canApprove = me.can('members.approve');
  const canDecide = me.can('approvals.decide');
  const live = useLiveVersion((e) => e.kind.startsWith('membership') || e.kind.startsWith('approval'));
  const data = useAsync(
    async (signal) => {
      if (!churchId) return { members: 0, approvals: 0, chats: 0 };
      const [members, approvals, chats] = await Promise.all([
        canApprove
          ? api.get<{ items: unknown[] }>(`/multichurch/churches/${churchId}/memberships?status=pending`, signal).then((r) => r.items.length).catch(() => 0)
          : Promise.resolve(0),
        canDecide
          ? api
              .get<{ items: { requestedBy: { personId: string } }[] }>(`/approvals/churches/${churchId}/requests?list=pending`, signal)
              .then((r) => r.items.filter((x) => x.requestedBy.personId !== me.personId).length)
              .catch(() => 0)
          : Promise.resolve(0),
        canChat
          ? api
              .get<{ items: { unread: number }[] }>(`/communication/churches/${churchId}/chats`, signal)
              .then((r) => r.items.reduce((sum, c) => sum + (c.unread || 0), 0))
              .catch(() => 0)
          : Promise.resolve(0),
      ]);
      return { members, approvals: approvals + members, chats };
    },
    [churchId, canApprove, canDecide, canChat, live, chatsAt],
  );
  return data.status === 'ready' ? data.data : { members: 0, approvals: 0, chats: 0 };
}
